import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button } from "@mui/material";
import axios from "axios";
import { Auth } from "aws-amplify";

import UploadIcon from '@mui/icons-material/Upload';
import DescriptionIcon from '@mui/icons-material/Description';

import OrgSampleSheet from "../../../data/OrgSampleSheet.csv"

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const AddAdminAndClass = ({setToastOpen, setToastType, masterAdminInfo, data, organization, setData, registerValidator, registerSubmitter, taskJobs }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});

  function downloadSample(){
    const link = document.createElement('a');
    link.download = "Org-Sample-Worksheet.csv";
    link.href = OrgSampleSheet;
    link.click();
  }

  const hasActiveTask = taskJobs.some(
    (task) => task.status !== 1
  );

  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0] || null;

    setSelectedFile(selectedFile);
    setErrors({});

    if (selectedFile) {
      setData({
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
      });
    }
  }, [setData]);

  const validateStep2 = useCallback(() => {
    const newErrors = {};

    if (!selectedFile) {
      newErrors.msg = "CSV file is required";
    }

    if (selectedFile && !selectedFile.name.toLowerCase().endsWith(".csv")) {
      newErrors.msg = "Only CSV files allowed";
    }

    if (selectedFile && selectedFile.size > MAX_FILE_SIZE) {
      newErrors.msg = "Max file size is 5MB";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [selectedFile]);

  useEffect(() => {
    registerValidator(validateStep2);
  }, [registerValidator, validateStep2]);

  const submitStep2 = useCallback(async () => {
    try {
      if (!selectedFile) return false;

      const session = await Auth.currentSession();
      const jwtToken = session.idToken.jwtToken;

      // file -> base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const result = reader.result;
          // strip: data:text/csv;base64,
          const encoded = result.split(",")[1];
          resolve(encoded);
        };

        reader.onerror = reject;

        reader.readAsDataURL(selectedFile);
      });

      const payload = {
        excelFile: base64,
        orgPrefix: organization.prefix
      };
      const masterAdminId = masterAdminInfo.masterAdmin.masterAdminId

      const responseData = await axios.post(
        `https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/masterAdmin/${masterAdminId}/schools`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );
      console.log(responseData)

      return true;
    } catch (err) {
      console.error(err);
      const responseData = err?.response?.data;
      const backendMessage = responseData?.message || "Upload failed";
      const backendErrors = responseData?.errors || [];

      setErrors({
        msg: backendMessage,
        errorsList: backendErrors
      });

      return false;
    }
  }, [masterAdminInfo, selectedFile, organization.prefix]);

  useEffect(() => {
    registerSubmitter(submitStep2);
  }, [registerSubmitter, submitStep2]);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Organization Details:</strong>

        <p style={{ marginTop: "0.5rem" }}>
          <b>Prefix:</b> {organization.prefix}
        </p>

        <p style={{ marginTop: "0.25rem" }}>
          <b>School Code Length:</b> {organization.schoolCodeLength}
        </p>

        <p style={{ marginTop: "0.25rem" }}>
          <b>School Code Format:</b>{" "}
          {
            String(organization.schoolCodeFormat) === "0"
              ? "NIC"
              : String(organization.schoolCodeFormat) === "1"
              ? "UDISE"
              : "SELF"
          }
        </p>

        <p style={{ marginTop: "0.25rem" }}>
          <b>Facilitator Login:</b>{" "}
          {
            String(organization.facilitatorLogin) === "1"
              ? "Single Facilitator"
              : "Facilitator Per Class"
          }
        </p>

        <p style={{ marginTop: "0.25rem" }}>
          <b>Single School:</b>{" "}
          {organization.singleSchool ? "Yes" : "No"}
        </p>

        <p style={{ marginTop: "0.75rem" }}>
          Upload CSV containing school rows matching these settings. Using the sample format <Button className="downloadSample" size="small" variant="contained" color="info" onClick={downloadSample} startIcon={<DescriptionIcon />}>Download Sample Sheet</Button>
        </p>
      </Alert>

      <form style={{ padding: "1rem", border: "2px dashed var(--border-color)", borderRadius: "10px", display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem", flexGrow: 1}}>
        <Button
          variant="text"
          component="label"
          disabled={hasActiveTask}
          // startIcon={}
          style={{
            textTransform: "none",
            gap: "1rem",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          <label
            className="form-label"
            htmlFor="customFile"
            style={{ display: "flex", alignItems: "center", gap: "1rem", cursor: "pointer", whiteSpace: "break-spaces" }}
          >
            <UploadIcon />
            {selectedFile ? selectedFile.name : "Please select a csv file for upload!"}
          </label>
          <input
            hidden
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="form-control btn"
            id="customFile"
          />
        </Button>
      </form>
      {hasActiveTask && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Another task is already processing!, previous task must be fully completed before starting a new one
        </Alert>
      )}
      {errors.msg && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {Array.isArray(errors.errorsList) ?
            <>            
              <h3>{errors.msg}</h3> 
              {errors.errorsList.map((e, i) => (
                <p key={i} style={{marginTop: "0.5rem"}}>{e}</p>
              ))}
            </>
          : 
            <h3>{errors.msg}</h3>
          }
        </Alert>
      )}
    </>
  );
};

export default AddAdminAndClass;